import { Stack } from '@startuptools/ui/Stack';
import { Avatar } from '@startuptools/ui/Avatar';
import { Typography } from '@startuptools/ui/Typography';
import { ColorPaletteProp } from '@mui/joy/styles';
import { ComponentProps, ReactElement, ReactNode } from 'react';
import { Trans, useLingui } from '@lingui/react';
import { Link, useLocation } from 'react-router-dom';
import { useStepperStep } from '../common/StepperStepContext';
import { GqlSeverity, GqlValidatorStatus } from '../../../graphql/base-types.graphql';
import ErrorIcon from '@mui/icons-material/Error';
import WarningIcon from '@mui/icons-material/Warning';
import CheckSharpIcon from '@mui/icons-material/CheckSharp';
import { Tooltip } from '@startuptools/ui/Tooltip';
import { Props } from '../common/StepperContext';

const StepIndicator = ({
  validatorStatus,
  isCurrentStep,
  stepNumber,
}: {
  validatorStatus?: GqlValidatorStatus | null;
  isCurrentStep: boolean;
  stepNumber: string;
}) => {
  const { i18n } = useLingui();
  const severityColor: ColorPaletteProp = validatorStatus?.severity === GqlSeverity.Error ? 'danger' : 'warning';
  const errorIcon =
    validatorStatus?.severity === GqlSeverity.Error ? <ErrorIcon color="error" /> : <WarningIcon color="warning" />;

  return (
    <Tooltip color={severityColor} title={validatorStatus?.error?.[i18n.locale === 'sv' ? 'sv' : 'en']?.short}>
      <Avatar
        variant="outlined"
        size="sm"
        color={
          validatorStatus?.hasError
            ? severityColor
            : isCurrentStep
              ? 'primary'
              : validatorStatus?.completed
                ? 'success'
                : 'neutral'
        }
      >
        {validatorStatus?.hasError ? (
          errorIcon
        ) : validatorStatus?.completed && !isCurrentStep ? (
          <CheckSharpIcon />
        ) : (
          stepNumber
        )}
      </Avatar>
    </Tooltip>
  );
};

export const NavStepperStep = ({
  label,
  path,
  validatorStatus,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  showTitle = false, // (value is used in NavStepperInner)
}: {
  label: ReactElement<ComponentProps<typeof Trans>>;
  path: string;
  children: ReactNode | ((props: Props) => ReactNode);
  showTitle?: boolean;
  index?: boolean; // used in NavStepper.tsx to set the first step as default
  validatorStatus: GqlValidatorStatus | null | undefined;
}) => {
  const location = useLocation();
  const { stepNumber } = useStepperStep();
  const isCurrentStep = location.pathname.endsWith(path);

  return (
    <>
      <Link to={`../${path}`} replace>
        <Stack
          direction="row"
          gap={1}
          alignItems="center"
          mx={1.5}
          p={0.5}
          sx={theme => ({
            backgroundColor: isCurrentStep ? theme.palette.background.level1 : 'inherit',
            cursor: 'pointer',
            borderRadius: 6,
          })}
        >
          <StepIndicator validatorStatus={validatorStatus} stepNumber={stepNumber} isCurrentStep={isCurrentStep} />
          <Stack gap={1}>
            <Typography level="title-sm">{label}</Typography>
          </Stack>
        </Stack>
      </Link>
    </>
  );
};
